import React, { useEffect, useState } from "react";
import { DeletePopupModal } from "../Components";
import { useNavigate, useSearchParams } from "react-router-dom";
import axiosConfig from "../Service/axiosConfig";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import DataTable from "react-data-table-component";
import AlertDeletePopupModal from "../Components/PopupModal/AlertDeletePopupModal";
import Select from "react-select";
import { useDebounce } from "../Utils/useDebounce";

const formatDateTime = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getRejectionReason = (row) =>
  row?.rejected_message ||
  row?.approval_message ||
  row?.rejection_reason ||
  row?.reject_reason ||
  row?.reason ||
  "";

const isRejected = (row) =>
  String(row?.approval_status || "").toLowerCase() === "rejected";

const getRequirementCreatedAt = (row) =>
  row?.cdate || row?.created_at || null;

const getRequirementUpdatedAt = (row) =>
  row?.mdate && row.mdate !== (row?.cdate || row?.created_at) ? row.mdate : null;

const APPROVAL_TABS = [
  { label: "Requested", value: "requested" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
  { label: "All", value: "all" },
];

const VALID_APPROVAL_TABS = APPROVAL_TABS.map((tab) => tab.value);

const getApprovalTabFromSearch = (searchParams) => {
  const value = (searchParams.get("approval_status") || "").toLowerCase();
  if (VALID_APPROVAL_TABS.includes(value)) return value;
  return "requested";
};

const RequirementsList = () => {
  const [Data, SetData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [RowPerPage, setRowPerPage] = useState(20);
  const [AlertDeletePopupType, SetAlertDeletePopupType] = useState("");
  const [deletePopupType, setDeletepopupType] = useState("");
  const [loading, setLoading] = useState(false);
  const [RowId, SetRowId] = useState();
  const [search, SetSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    approval_status: getApprovalTabFromSearch(searchParams),
    RequirementStatus: searchParams.get("requirement_status") || "",
  });
  const navigate = useNavigate();
  const debouncedQuery = useDebounce(search, 600);
  const showRequirementStatusFilter = formData.approval_status === "approved" || formData.approval_status === "all";

  const GetData = async (page = currentPage, pageSize = RowPerPage) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      });
      if (formData.approval_status && formData.approval_status !== "all") {
        params.set("approval_status", formData.approval_status);
      }
      if (formData.RequirementStatus) {
        params.set("requirement_status", formData.RequirementStatus);
      }
      if (debouncedQuery) {
        params.set("search", debouncedQuery.trim());
      }
      const response = await axiosConfig.get(`/accounts/audition-requirements/?${params.toString()}`);
      SetData(response.data.results || []);
      setTotalRows(response.data.count || 0);
      setCurrentPage(page);
    } catch (error) {
      console.log(error);
      SetData([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (row) => {
    navigate(`/dashboard/masters/audition-requirements/${row}/edit`);
  };

  const columns = [
    {
      name: "S.No",
      selector: "id",
      cell: (row, i) => (currentPage - 1) * RowPerPage + i + 1,
      sortable: false,
      width: "52px",
      compact: true,
    },
    {
      name: "Id",
      selector: "id",
      cell: (row) => (
        <div className="req-cell">
          AD{row?.audition?.id || ""}-RD{row.id}
        </div>
      ),
      sortable: true,
      grow: 0.7,
      wrap: true,
    },
    {
      name: "ICC ID",
      selector: "id",
      cell: (row) => (
        <div className="req-cell">
          {row?.audition?.profile?.first_name || ""}
          <br />
          {row?.audition?.profile?.hcc_id || ""}
        </div>
      ),
      sortable: true,
      grow: 0.8,
      wrap: true,
    },
    {
      name: "Project",
      selector: "project_title",
      cell: (row) => (
        <div className="req-cell">
          <b>{row?.audition?.banner_name || ""}</b>
          <br />
          {row?.audition?.project_title || ""}
        </div>
      ),
      sortable: true,
      grow: 1.4,
      wrap: true,
    },
    {
      name: "Apps",
      selector: "applications",
      cell: (row) => row?.applications,
      sortable: true,
      width: "52px",
      compact: true,
    },
    {
      name: "Character",
      selector: "character",
      cell: (row) => (
        <div className="req-cell">
          {row?.character || ""}
          <br />
          <b>
            {row.from_age}-{row.to_age}({row.gender})
          </b>
        </div>
      ),
      sortable: true,
      grow: 0.9,
      wrap: true,
    },
    {
      name: "Status",
      selector: "approval_status",
      cell: (row) => {
        const approval = String(row?.approval_status || "").toLowerCase();
        const showLiveClosed = approval === "approved";
        const statusLabel = showLiveClosed
          ? `${row?.approval_status || "--"}/${row?.requirement_status || "--"}`
          : (row?.approval_status || "--");
        const reason = isRejected(row) ? getRejectionReason(row) : "";
        return (
          <div className="req-cell" style={{ textAlign: "left" }}>
            <div>
              {statusLabel}
              {row?.priority != null ? ` (${row.priority})` : ""}
            </div>
            {reason ? (
              <div className="req-reject-reason" title={reason}>
                {reason}
              </div>
            ) : null}
          </div>
        );
      },
      sortable: true,
      grow: 1,
      wrap: true,
    },
    {
      name: "Date",
      selector: "created_at",
      cell: (row) => {
        const createdAt = getRequirementCreatedAt(row);
        const updatedAt = getRequirementUpdatedAt(row);
        return (
          <div className="req-cell" style={{ fontSize: 11, lineHeight: 1.35 }}>
            <div>{formatDateTime(createdAt)}</div>
            {updatedAt ? (
              <div style={{ color: "#6b7280" }}>Upd: {formatDateTime(updatedAt)}</div>
            ) : null}
          </div>
        );
      },
      sortable: true,
      grow: 0.7,
      wrap: true,
    },
    {
      name: "",
      width: "44px",
      compact: true,
      selector: (row) => {
        const CancelAlerrt = () => {
          SetAlertDeletePopupType("error");
        };
        const HandleDelete = async (id) => {
          await axiosConfig
            .delete(`/accounts/audition-requirements/${id}/`)
            .then(() => {
              SetAlertDeletePopupType("error");
              setDeletepopupType("success");
              GetData(currentPage);
            })
            .catch((error) => {
              setDeletepopupType("error");
              SetAlertDeletePopupType("error");
              console.log(error);
            });
        };
        return (
          <>
            <MdOutlineRemoveRedEye
              className="dashboard-tabe-data-actions"
              onClick={() => handleRowClick(row.id)}
            />
            {AlertDeletePopupType === "success" && (
              <AlertDeletePopupModal AlertDeletePopupType={AlertDeletePopupType} HandleDelete={HandleDelete} CancelAlerrt={CancelAlerrt} row={RowId} />
            )}
          </>
        );
      },
    },
  ];

  const RequirementStatus = [
    { label: "All", value: "" },
    { label: "Live", value: "live" },
    { label: "Closed", value: "closed" },
  ];

  const syncListParams = (nextForm) => {
    const next = new URLSearchParams(searchParams);
    next.set("approval_status", nextForm.approval_status || "requested");
    if (nextForm.RequirementStatus) {
      next.set("requirement_status", nextForm.RequirementStatus);
    } else {
      next.delete("requirement_status");
    }
    setSearchParams(next, { replace: true });
  };

  const handleLiveStatusChange = (selected) => {
    setFormData((prevFormData) => {
      const nextForm = {
        ...prevFormData,
        RequirementStatus: selected?.value || "",
      };
      syncListParams(nextForm);
      return nextForm;
    });
  };

  const handleApprovalTab = (value) => {
    setFormData((prev) => {
      const hideLiveClosed = value === "requested" || value === "rejected";
      const nextForm = {
        ...prev,
        approval_status: value,
        RequirementStatus: hideLiveClosed ? "" : prev.RequirementStatus,
      };
      syncListParams(nextForm);
      return nextForm;
    });
  };

  useEffect(() => {
    const current = (searchParams.get("approval_status") || "").toLowerCase();
    if (!VALID_APPROVAL_TABS.includes(current)) {
      const next = new URLSearchParams(searchParams);
      next.set("approval_status", formData.approval_status || "requested");
      setSearchParams(next, { replace: true });
    }
  }, []);

  useEffect(() => {
    GetData(1);
  }, [formData.approval_status, formData.RequirementStatus, debouncedQuery, RowPerPage]);

  const tableCustomStyles = {
    table: {
      style: {
        width: "100%",
        minWidth: 0,
      },
    },
    tableWrapper: {
      style: {
        overflowX: "hidden",
        display: "block",
      },
    },
    cells: {
      style: {
        justifyContent: "center",
        whiteSpace: "normal",
        overflow: "hidden",
        minWidth: 0,
        paddingLeft: "6px",
        paddingRight: "6px",
        paddingTop: "8px",
        paddingBottom: "8px",
      },
    },
    headCells: {
      style: {
        color: "rgb(33,37,41)",
        fontSize: "12px",
        fontWeight: 500,
        lineHeight: "18px",
        justifyContent: "center",
        padding: "6px",
        minWidth: 0,
        whiteSpace: "normal",
      },
    },
  };

  return (
    <>
      <div className="child-sidenav">
        <div className="childnav-content">
          <div className="marginTop requirements-table">
            <div className="requirement-list-toolbar">
              <div className="requirement-status-tabs" role="tablist" aria-label="Approval status">
                {APPROVAL_TABS.map((tab) => (
                  <button
                    key={tab.value || "all"}
                    type="button"
                    role="tab"
                    aria-selected={formData.approval_status === tab.value}
                    className={`requirement-status-tab ${formData.approval_status === tab.value ? "active" : ""}`}
                    onClick={() => handleApprovalTab(tab.value)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="d-flex gap-2 requirements-filters flex-wrap">
                {showRequirementStatusFilter && (
                <div style={{ width: 180 }}>
                  <Select
                    options={RequirementStatus}
                    onChange={handleLiveStatusChange}
                    value={RequirementStatus.find((opt) => opt.value === formData.RequirementStatus)}
                    placeholder="Live / Closed"
                    isClearable
                    name="RequirementStatus"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </div>
                )}
                <div style={{ minWidth: 260, flex: 1 }}>
                  <input
                    className="form-control"
                    placeholder="Search by RD/AD id, character, title, HCCID..."
                    value={search}
                    onChange={(e) => SetSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <DataTable
              columns={columns}
              data={Data}
              pagination
              paginationTotalRows={totalRows}
              paginationPerPage={RowPerPage}
              paginationDefaultPage={currentPage}
              onChangePage={(page) => GetData(page)}
              paginationPerPageOptions={[10, 20, 25, 30, 40, 50]}
              paginationRowsPerPageOptions={[10, 20, 25, 30, 40, 50]}
              onChangeRowsPerPage={(rowsPerPageChange) => setRowPerPage(rowsPerPageChange)}
              paginationServer
              dense
              customStyles={tableCustomStyles}
              progressPending={loading}
              progressComponent={
                <div className="loader-container">
                  <img src={require("../Components/FormParser/images/loader2.gif")} alt="Loading..." />
                </div>
              }
            />
          </div>
        </div>
      </div>
      {deletePopupType !== "" && <DeletePopupModal setDeletepopupType={setDeletepopupType} deletePopupType={deletePopupType} />}
    </>
  );
};

export { RequirementsList };
